import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Storefront, PaperPlaneTilt, CheckCircle, Warning, ArrowsClockwise, ClockCounterClockwise } from "@phosphor-icons/react";

export default function AdminRestaurantNotifications() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/admin/restaurant-notifications/upcoming")
      .then(r => setRows(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const sendNow = async (instanceId) => {
    if (!window.confirm("Send the 24-hour advance email to every restaurant stop on this tour right now?")) return;
    setSending(s => ({ ...s, [instanceId]: true }));
    try {
      const r = await api.post(`/admin/instances/${instanceId}/notify-restaurants`);
      const sent = r.data.sent?.length || 0;
      const skipped = r.data.skipped?.length || 0;
      alert(`Sent ${sent} email${sent === 1 ? "" : "s"}.${skipped ? ` Skipped ${skipped} (no email configured on those stops).` : ""}`);
      load();
    } catch (e) {
      alert("Send failed: " + (e?.response?.data?.detail || ""));
    } finally {
      setSending(s => ({ ...s, [instanceId]: false }));
    }
  };

  return (
    <div className="p-5 md:p-12" data-testid="admin-restaurant-notifications">
      <p className="label-eyebrow text-ms-stone">Operations</p>
      <h1 className="serif text-3xl md:text-4xl text-ms-earth mt-2 flex items-center gap-3">
        <Storefront size={32} weight="duotone" className="text-ms-brass" /> Restaurant notifications
      </h1>
      <p className="mt-2 text-sm text-ms-stone max-w-2xl">
        Auto-sends a 24-hour heads-up email to each restaurant stop on every tour (guest count, arrival time, dietary notes, special occasions, your contact info). Configure the recipient email + arrival offset on each tour's Restaurant Stops section. You can also manually send any tour's notifications now.
      </p>

      <div className="mt-4">
        <button onClick={load} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-ms-edge rounded-md text-ms-stone hover:bg-ms-cream" data-testid="rn-refresh">
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>

      <div className="mt-6 space-y-3">
        {loading ? (
          <p className="text-sm text-ms-stone">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-ms-stone bg-white border border-ms-edge rounded-2xl p-6">No tours scheduled in the next 7 days.</p>
        ) : (
          rows.map(r => (
            <article key={r.instance_id} className="bg-white border border-ms-edge rounded-2xl p-5" data-testid={`rn-row-${r.instance_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="serif text-xl text-ms-earth">{r.tour_name}</p>
                  <p className="text-sm text-ms-stone">{new Date(r.starts_at).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
                  <p className="mt-1 text-xs text-ms-stone">
                    {r.stops_total} stop{r.stops_total === 1 ? "" : "s"}
                    {r.stops_with_email < r.stops_total && (
                      <span className="ml-2 inline-flex items-center gap-1 text-ms-warn">
                        <Warning size={11} weight="fill" /> {r.stops_total - r.stops_with_email} missing email
                      </span>
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <button
                    onClick={() => sendNow(r.instance_id)}
                    disabled={r.stops_with_email === 0 || sending[r.instance_id]}
                    className="inline-flex items-center gap-2 px-4 py-2 text-xs label-eyebrow bg-ms-earth text-ms-cream rounded-md hover:bg-ms-brass disabled:opacity-40"
                    data-testid={`rn-send-${r.instance_id}`}
                  >
                    <PaperPlaneTilt size={12} /> {sending[r.instance_id] ? "Sending…" : "Send now"}
                  </button>
                  <p className="text-[11px] text-ms-stone mt-2 flex items-center justify-end gap-1">
                    <ClockCounterClockwise size={11} />
                    {r.last_sent_at ? `Last sent ${new Date(r.last_sent_at).toLocaleString()} (${r.last_trigger || "?"})` : "Not sent yet"}
                  </p>
                </div>
              </div>

              {r.recent_log.length > 0 && (
                <div className="mt-4 border-t border-ms-edge pt-3">
                  <p className="label-eyebrow text-ms-stone text-[10px]">Send log</p>
                  <ul className="mt-1.5 space-y-1">
                    {r.recent_log.map(l => (
                      <li key={l.id} className="text-xs text-ms-stone flex items-center gap-2" data-testid={`rn-log-${l.id}`}>
                        {l.ok ? <CheckCircle size={12} weight="fill" className="text-ms-success" /> : <Warning size={12} weight="fill" className="text-ms-danger" />}
                        <span className="text-ms-earth">{l.restaurant_name}</span>
                        <span>· {l.recipient || "(no email)"}</span>
                        <span>· {new Date(l.sent_at).toLocaleString()}</span>
                        <span className="text-ms-brass">· {l.trigger}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </article>
          ))
        )}
      </div>
    </div>
  );
}
